import React, { useEffect, useRef, useState } from "react";
import { logResponse } from "../utils/logger";
import SubmitButton from "../components/SubmitButton";
import { useParams } from "react-router-dom";
import {
  fetchGetSubscription,
  fetchGetCustomer,
  fetchUpdateUsagePoints
} from "../api";
import iconPay from "../assets/img/icon_pay.svg";

const getProductUnit = (productName) => {
  if (productName.includes("ジュレ") && productName.includes("5包入り")) return "袋";
  if (productName.includes("ジュレ")) return "箱";
  if (productName.includes("ビフィずっと")) return "袋";
  if (productName.includes("山野草ファイバー")) return "袋";
  if (productName.includes("ぷるるん酵素")) return "袋";
  return "本";
};

const UsePoint = () => {
  const { subscription_number } = useParams();
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const [accessToken, setAccessToken] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [subscriptionItems, setSubscriptionItems] = useState([]);

  // ★ 次回利用ポイント
  const [nextUsagePoints, setNextUsagePoints] = useState("0");

  // ★ 次々回以降の自動利用ポイント
  const [fixedUsagePoints, setFixedUsagePoints] = useState("0");

  const [useFixed, setUseFixed] = useState(false);
  const [availablePoints, setAvailablePoints] = useState(0);

  // ★ 成功／失敗メッセージ
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setAccessToken(token);

    if (!token || !subscription_number) return;

    // 定期情報取得
    fetchGetSubscription(subscription_number, token).then((data) => {
      logResponse("subscription:", data);

      if (Array.isArray(data) && data.length > 0) {
        const sub = data[0];
        setSubscription(sub);
        setSubscriptionItems(data);

        // 自動利用設定済みの場合は自動利用側に値を反映、未設定なら次回利用側に反映
        if (sub.point_usage_flag === "する") {
          setUseFixed(true);
          setFixedUsagePoints(sub.usage_points?.toString() || "0");
        } else {
          setNextUsagePoints(sub.usage_points?.toString() || "0");
        }
      }
    });

    // 顧客情報から利用可能ポイント取得
    fetchGetCustomer(token).then((data) => {
      logResponse("customer:", data);

      if (data && typeof data.total_points === "number") {
        setAvailablePoints(data.total_points);
      }
    });
  }, [subscription_number]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;

    setErrorMessage("");
    setSuccessMessage("");

    // バリデーション
    if (!useFixed && nextUsagePoints === "0") {
      setErrorMessage("次回利用ポイントを選択してください。");
      return;
    }

    if (useFixed && fixedUsagePoints === "0") {
      setErrorMessage("次々回以降の自動利用ポイントを選択してください。");
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const data = {
        usage_points: nextUsagePoints,          // 次回利用ポイント
        auto_usage_points: fixedUsagePoints,    // 次々回以降ポイント
        use_fixed: useFixed ? "1" : "0",        // 自動利用するか（する：1）
      };

      logResponse("update request:", data);

      const res = await fetchUpdateUsagePoints(subscription_number, accessToken, data);

      // ★ デバッグログ（レスポンス内容が不明なので必須）
      logResponse("update response:", res);

      // ★ 成功判定（detailはエラー応答なので失敗扱い）
      if (res && !res.detail) {
        setSuccessMessage("ポイント設定を更新しました。更新までしばらくお待ちください。");
        setErrorMessage("");
      } else {
        setErrorMessage("ポイント設定の更新に失敗しました。");
        setSuccessMessage("");
      }

    } catch (err) {
      console.error("送信エラー:", err);
      setErrorMessage("通信エラーが発生しました。");
      setSuccessMessage("");
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // ▼ 1本分のポイント（定期情報の1行目の単価）
  const firstItem = subscriptionItems[0];
  const oneBottlePoints = firstItem?.unit_price || 0;
  const firstQuantity = firstItem?.quantity || 0;
  const totalAmount = firstItem?.total_amount || 0;
  const hasMultipleLines = subscriptionItems.length >= 2;
  const unit = getProductUnit(firstItem?.product_name || "");

  // ▼ 次回利用ポイント
  const nextTimeOptions = [];
  const nextSeen = new Set();
  // 1本分〜N本分（1行目の数量に応じて）
  for (let n = 1; n <= firstQuantity; n++) {
    const value = oneBottlePoints * n;
    if (value > 0 && value <= availablePoints && !nextSeen.has(value)) {
      nextTimeOptions.push({ value, label: `${n}${unit}分（${value}ポイント）` });
      nextSeen.add(value);
    }
  }
  // 総額分（2行以上のときは常に表示。N本分と同値でも別行として表示する）
  if (hasMultipleLines && totalAmount > 0 && totalAmount <= availablePoints) {
    nextTimeOptions.push({ value: totalAmount, label: `総額分（${totalAmount}ポイント）` });
    nextSeen.add(totalAmount);
  }
  // 1000〜（100刻み、保有ポイント上限）
  for (let pt = 1000; pt <= availablePoints; pt += 100) {
    if (!nextSeen.has(pt)) {
      nextTimeOptions.push({ value: pt, label: `${pt}ポイント` });
      nextSeen.add(pt);
    }
  }
  // 保存値が選択肢に存在しない場合は動的追加（先頭に挿入）
  const nextSavedValue = parseInt(nextUsagePoints, 10);
  if (nextSavedValue > 0 && !nextSeen.has(nextSavedValue)) {
    nextTimeOptions.unshift({ value: nextSavedValue, label: `${nextSavedValue}ポイント` });
  }

  // ▼ 次々回以降の自動利用ポイント
  const fixedOptions = [];
  const fixedSeen = new Set();
  // 1本分・2本分（1行目の数量が2以上のとき2本分も追加、最大2まで）
  const fixedMaxBottles = Math.min(firstQuantity, 2);
  for (let n = 1; n <= fixedMaxBottles; n++) {
    const value = oneBottlePoints * n;
    if (value > 0 && !fixedSeen.has(value)) {
      fixedOptions.push({ value, label: `${n}${unit}分（${value}ポイント）` });
      fixedSeen.add(value);
    }
  }
  // 1000〜2000（100刻み）
  for (let pt = 1000; pt <= 2000; pt += 100) {
    if (!fixedSeen.has(pt)) {
      fixedOptions.push({ value: pt, label: `${pt}ポイント` });
      fixedSeen.add(pt);
    }
  }
  // 3000〜10000（1000刻み）
  for (let pt = 3000; pt <= 10000; pt += 1000) {
    if (!fixedSeen.has(pt)) {
      fixedOptions.push({ value: pt, label: `${pt}ポイント` });
      fixedSeen.add(pt);
    }
  }
  // 保存値が選択肢に存在しない場合は動的追加（先頭に挿入）
  const fixedSavedValue = parseInt(fixedUsagePoints, 10);
  if (fixedSavedValue > 0 && !fixedSeen.has(fixedSavedValue)) {
    fixedOptions.unshift({ value: fixedSavedValue, label: `${fixedSavedValue}ポイント` });
  }

  return (
    <section className="section point-section">
      <h2 className="section-title">
        <i>
          <img src={iconPay} alt="" />
        </i>
        ポイント利用
      </h2>

      <div className="point-group">
        <div className="group-wrapper">
          {subscription ? (
            <>
              <p className="point-title">保有ポイント</p>
              <p className="point-num">
                <span className="num">{availablePoints}</span> ポイント
              </p>

              <form ref={formRef} onSubmit={handleSubmit}>
                <div className="point-select">

                  {/* ▼ 次回利用ポイント（常に表示） */}
                  <label>
                    次回利用するポイント
                    <select
                      name="next_usage_points"
                      value={nextUsagePoints}
                      onChange={(e) => setNextUsagePoints(e.target.value)}
                    >
                      <option value="0">選択してください</option>
                      {nextTimeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* ▼ 自動利用ポイント設定済みメッセージ */}
                  {subscription.point_usage_flag === "する" && subscription.usage_points > 0 && (
                    <p style={{ marginTop: "20px", fontWeight: "bold", color: "#EF8200" }}>
                      すでに下記のポイント自動利用の設定がされています（{subscription.usage_points}ポイント）
                    </p>
                  )}

                  {/* ▼ チェックボックス */}
                  <div className="point-next" style={{ marginTop: "20px" }}>
                    <label>
                      <input
                        type="checkbox"
                        name="use_fixed"
                        checked={useFixed}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseFixed(checked);
                          setFixedUsagePoints("0"); // 初期化
                        }}
                      />
                      以降、決めたポイントを超えると自動利用する
                    </label>
                  </div>

                  {/* ▼ チェック時に“下に”表示されるセレクト */}
                  {useFixed && (
                    <label style={{ marginTop: "15px", display: "block" }}>
                      自動利用するポイント
                      <select
                        name="fixed_usage_points"
                        value={fixedUsagePoints}
                        onChange={(e) => setFixedUsagePoints(e.target.value)}
                      >
                        <option value="0">選択してください</option>
                        {fixedOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <div className="btn-green">
                    <SubmitButton
                      isSubmitting={isSubmitting}
                      label="ポイントを利用する"
                      className="btn-green"
                      wrap={false}
                    />
                  </div>
                  {/* ▼ 成功／失敗メッセージ（ボタンの下） */}
                  {successMessage && (
                    <p className="text-success mt10 fs120" style={{ fontWeight: "bold", color: "green" }}>
                      {successMessage}
                    </p>
                  )}
                  {errorMessage && (
                    <p className="mt10 fs120" style={{ color: "red", fontWeight: "bold" }}>
                      {errorMessage}
                    </p>
                  )}
                </div>
              </form>
            </>
          ) : (
            <p>定期情報を読み込み中...</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default UsePoint;
